import { SOCIAL_TOPICS } from './types';

export type DiscoverySource = { tags: readonly string[]; body?: string; concepts?: readonly string[] };
const categories = new Set(SOCIAL_TOPICS.map(topic => topic.toLowerCase()));
// Grammar and generic posting language delimit phrases; subjects are never
// selected from a fixed vocabulary. The same extraction is used when opening a topic.
const stopwords = new Set(('a an the and or but if while as at by for from in into of on onto to with without ' +
  'is are was were be been being am do does did can could would should will may might must have has had ' +
  'i me my mine we us our ours you your yours he him his she her hers it its they them their theirs ' +
  'this that these those there here what which who whom when where why how not no yes than then ' +
  'about above below between through during over under after before again also very just more most ' +
  'some any all each both other such so too only own same up out off now today yesterday tomorrow ' +
  'new latest first last great really much many still even well get gets got getting make makes made ' +
  'use uses used using try trying test testing take takes taking find finding see look looking learn ' +
  'learning share shares sharing post posts posted posting update updates updated hello thanks thank ' +
  'thoughts thoughtful useful creative perspective idea ideas guide behind something everything').split(/\s+/));

export function discoveryTag(value: string): string | null {
  const tag = value.normalize('NFKC').toLowerCase().trim().replace(/^#/, '').replace(/[\s-]+/g, '_');
  return tag.length <= 32 && /^[\p{L}\p{N}][\p{L}\p{N}_]*$/u.test(tag) ? tag : null;
}

export function discoverPostTopics(source: DiscoverySource) {
  const topics = new Map<string, number>();
  const add = (value: string, weight: number) => {
    const tag = discoveryTag(value);
    if (tag) topics.set(tag, Math.max(topics.get(tag) || 0, weight * (categories.has(tag) ? .5 : 1)));
  };
  for (const tag of source.tags.slice(0, 8)) add(tag, 1.5);
  for (const concept of (source.concepts || []).slice(0, 12)) add(concept, 1.35);
  const caption = (source.body || '').slice(0, 2200);
  for (const match of caption.matchAll(/(?:^|\s)#([\p{L}\p{N}_]{1,32})(?=\s|$|[.,!?])/gu)) add(match[1], 1.5);
  const text = caption.replace(/https?:\/\/\S+|www\.\S+|\S+@\S+|@[\p{L}\p{N}_.]+/gu, '.').replace(/#[\p{L}\p{N}_]+/gu, '.');
  const tokens = (text.normalize('NFKC').toLowerCase().match(/[\p{L}\p{N}][\p{L}\p{N}_-]*|[.!?;:,\n]/gu) || []).slice(0, 220);
  const word = (token: string) => token.length >= 3 && /\p{L}/u.test(token) && !stopwords.has(token);
  for (let start = 0; start < tokens.length; start++) {
    const phrase: string[] = [];
    for (let end = start; end < Math.min(start + 3, tokens.length) && word(tokens[end]); end++) {
      phrase.push(tokens[end]); add(phrase.join('_'), .65 + .2 * (phrase.length - 1));
    }
  }
  return [...topics].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).slice(0, 96).map(([tag, weight]) => ({ tag, weight }));
}
